import { Plus, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { useToast, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from 'randmarcomps';
import { ManufacturerCategory, usePutV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeMutation } from 'randmar-api-client';
import { Category } from "../../stores/category/Category";
import { useManufacturer } from "../../contexts/WithManufacturer";

interface IProps {
  categories?: (ManufacturerCategory | Category)[];
  renderTrigger?: (options: { openDialog: () => void }) => React.ReactNode;
}

const isLegacyCategory = (category: ManufacturerCategory | Category): category is Category => 'categoryName' in category;

const CreateCategory: React.FC<IProps> = ({ categories, renderTrigger }) => {
  const { applicationId } = useManufacturer();
  const [categoryName, setCategoryName] = useState("");
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [createCategory, { isLoading: isCreating }] = usePutV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeMutation();
  const handleOpen = () => setOpen(true);

  const handleCancel = () => {
    setOpen(false);
    setCategoryName("");
  };

  const handleCreateCategory = async () => {
    try {
      await createCategory({
        routeManufacturerId: applicationId,
        categoryCode: categoryName,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `Category ${categoryName} was successfully created.` });
      setCategoryName("");
      setOpen(false);
    } catch {
      toast({ variant: "destructive", title: "Error", description: `Couldn't create the category ${categoryName}.` });
    }
  };

  const trigger = renderTrigger
    ? renderTrigger({ openDialog: handleOpen })
    : (
      <Button onClick={handleOpen}>
        <Plus className="mr-2 h-4 w-4" /> New Category
      </Button>
    );

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Category</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="category-name">Category name</Label>
              <Input
                id="category-name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter category name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              disabled={isCreating || categoryName === "" || categories?.some(c => (isLegacyCategory(c) ? c.categoryName : c.CategoryName)?.toLowerCase() === categoryName.toLowerCase())}
              onClick={handleCreateCategory}
            >
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateCategory;
